import * as React from 'react';

import { cn } from '../lib/utils';

export interface OEEGaugeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** OEE percentage (0-100) */
  value: number;
  /** Availability component (0-100) */
  availability: number;
  /** Performance component (0-100) */
  performance: number;
  /** Quality component (0-100) */
  quality: number;
  /** Display mode */
  mode?: 'compact' | 'full';
  /** Gauge diameter in pixels */
  size?: number;
  /** Animate on mount */
  animated?: boolean;
}

/**
 * Circular OEE (Overall Equipment Effectiveness) gauge showing availability,
 * performance, and quality segments. Uses design tokens (ISA-101) for colors:
 *   >= 85%  → `--state-running` (green)
 *   >= 60%  → `--alarm-minor` (amber)
 *   <  60%  → `--alarm-critical` (red)
 *
 * @example
 * ```tsx
 * <OEEGauge value={82.5} availability={90} performance={85} quality={95} />
 * ```
 */
export const OEEGauge = React.forwardRef<HTMLDivElement, OEEGaugeProps>(
  (
    {
      value,
      availability,
      performance,
      quality,
      mode = 'full',
      size = 240,
      animated = true,
      className,
      ...props
    },
    ref
  ) => {
    const radius = size / 2 - 20;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    const toneFor = (v: number) =>
      v >= 85
        ? 'var(--state-running)'
        : v >= 60
        ? 'var(--alarm-minor)'
        : 'var(--alarm-critical)';

    const valueTone = toneFor(value);

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex flex-col items-center justify-center gap-4',
          className
        )}
        {...props}
      >
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="-rotate-90">
            {/* Background track */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="hsl(var(--muted))"
              strokeWidth={16}
            />
            {/* Value arc */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={`hsl(${valueTone})`}
              strokeWidth={16}
              strokeDasharray={circumference}
              strokeDashoffset={circumference - (value / 100) * circumference}
              strokeLinecap="round"
              style={{
                transition: animated
                  ? 'stroke-dashoffset 1s ease-out'
                  : undefined,
              }}
            />
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span
              className="text-5xl font-bold tabular-nums"
              style={{ color: `hsl(${valueTone})` }}
            >
              {Math.round(value)}
              <span className="text-2xl">%</span>
            </span>
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              OEE
            </span>
          </div>
        </div>

        {mode === 'full' && (
          <div className="grid w-full grid-cols-3 gap-2 text-center">
            <ComponentRow label="Availability" value={availability} />
            <ComponentRow label="Performance" value={performance} />
            <ComponentRow label="Quality" value={quality} />
          </div>
        )}
      </div>
    );
  }
);

OEEGauge.displayName = 'OEEGauge';

function ComponentRow({ label, value }: { label: string; value: number }) {
  const tone =
    value >= 85
      ? 'var(--state-running)'
      : value >= 60
      ? 'var(--alarm-minor)'
      : 'var(--alarm-critical)';
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className="text-lg font-semibold tabular-nums"
        style={{ color: `hsl(${tone})` }}
      >
        {Math.round(value)}%
      </div>
    </div>
  );
}
